import { Button } from "@mui/material";
import { setCartItems } from "../../lib/Checkout/cart.ts";
import React from "react";
import ProductManager from "../../lib/Product/ProductManager.ts";
import { appendTrackingParams } from "../../utils/cookieManager.ts";
import { calculateQuantityForAppProduct } from "../../utils/cart.ts";
import type { CartLineItem, SelectedProducts } from "../../types/product";
import { createLogger } from "../../lib/logger.ts";
import { useAddressData } from "../../context/FunnelDataContext.tsx";
import { ProductType } from "@shared/interfaces";

const logger = createLogger("CheckoutButton");

type CheckoutButtonProps = {
    moduleCount: number;
    selectedProducts: SelectedProducts;
    pickedDate: string | null;
    kwPerYearAndModule: number;
    powerConsumption: number;
    costsPerKwh: number;
};

const itemListOrder = [
    ProductType.SOLAR_PANEL,
    ProductType.BATTERY_STORAGE,
    ProductType.INVERTER,
    ProductType.ACCESSORY,
    ProductType.MOUNTING_SYSTEM,
    ProductType.SERVICE_PACKAGE,
    ProductType.OTHERS
];

const getCartLineItemsFromSelectedProducts = (products: SelectedProducts, moduleCount: number, kwPerYearAndModule: number): CartLineItem[] => {
    return Object.values(products)
    .filter((product) => product.merchandiseId)
    .sort((a, b) => itemListOrder.indexOf(b.type) - itemListOrder.indexOf(a.type))
    .map((product) => (
        {
            merchandiseId: product.merchandiseId,
            quantity: calculateQuantityForAppProduct(product, moduleCount, kwPerYearAndModule)
        } as CartLineItem
    ));
}

const CheckoutButton: React.FC<CheckoutButtonProps> = ({
    moduleCount,
    selectedProducts,
    pickedDate,
    kwPerYearAndModule,
    powerConsumption,
    costsPerKwh
}: CheckoutButtonProps) => {
    const addressData = useAddressData()!;
    const onClick = async () => {
        try {
            const cartItems = getCartLineItemsFromSelectedProducts(selectedProducts, moduleCount, kwPerYearAndModule);
            logger.debug('Cart Items', { cartItems });

            const costSummary = ProductManager.getCostsCalculation(
                selectedProducts,
                moduleCount,
                kwPerYearAndModule,
                powerConsumption,
                costsPerKwh,
                addressData.currentAddress
            );

            const pvSize = (moduleCount * (selectedProducts[ProductType.SOLAR_PANEL]?.powerClass || 0)) / 1000;
            const response = await setCartItems(cartItems, costSummary.economy?.totalCost || 0, pvSize, pickedDate);
            if (!response.ok) {
                logger.error("Error setting items to cart");
                return;
            }

            const result = await response.json();

            if (!result.checkoutUrl) {
                logger.error("No checkout URL returned from response");
                return;
            }

            window.open(appendTrackingParams(result.checkoutUrl), "_blank");
        } catch (error) {
            logger.error("Failed to create cart", { error });
        }
    };

    return (
        <Button
            variant="contained"
            onClick={onClick}
            sx={{
                backgroundColor: "#ff8900",
                borderRadius: "39px",
                py: "16px",
                fontSize: 18,
                fontFamily: '"Inter", sans-serif',
                color: "#00031f",
                flexGrow: 1
            }}
        >
            Risikofrei starten
        </Button>
    );
};

export default CheckoutButton;
